import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSubscriptionStatus } from "../hooks/useStripe";
import axios from "axios";

const baseUrl = process.env.REACT_APP_API_CONFIG;

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [countdown, setCountdown] = useState(5);
  const { status, refetch } = useSubscriptionStatus();
  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const verifyCalledRef = useRef(false);

  // Verify and sync subscription from Lemon Squeezy on mount
  // This is the PRIMARY way to create subscription - webhook is just backup
  useEffect(() => {
    if (verifyCalledRef.current) return;
    verifyCalledRef.current = true;

    const verifySubscription = async () => {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        setVerifying(false);
        setVerifyError("Not logged in");
        return;
      }

      try {
        // Call verify endpoint to sync subscription from Lemon Squeezy
        const response = await axios.post(
          `${baseUrl}/subscription/lemonsqueezy/verify`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log("[SubscriptionSuccess] Verify response:", response.data);

        if (response.data?.success) {
          // Subscription synced, now refetch status to update UI
          await refetch();
        }
      } catch (err: any) {
        console.error("[SubscriptionSuccess] Verify failed:", err);
        // Don't show error to user - subscription might have been created by webhook
        // Just try to refetch status
        await refetch();
      } finally {
        setVerifying(false);
      }
    };

    verifySubscription();
  }, [refetch]);

  // Countdown to redirect - only start after verification is done
  useEffect(() => {
    if (verifying) return; // Don't start countdown while verifying

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, verifying]);

  // Show loading while verifying
  if (verifying) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto bg-[#3af062] rounded-full flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">
            Activating your subscription...
          </h1>
          <p className="text-gray-400">
            Please wait while we confirm your payment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Success animation */}
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto bg-[#3af062] rounded-full flex items-center justify-center animate-bounce">
            <svg
              className="w-12 h-12 text-black"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        {/* Success message */}
        <h1 className="text-3xl font-bold text-white mb-4">
          Welcome to {status?.tier === "pro" ? "Pro" : "Basic"}!
        </h1>

        <p className="text-gray-400 mb-8">
          Your subscription has been activated successfully. You now have access
          to all {status?.tier === "pro" ? "premium" : "basic"} features.
        </p>

        {/* Feature highlights */}
        <div className="bg-[#1a1a1a] rounded-2xl p-6 mb-8 text-left">
          <h3 className="text-white font-semibold mb-4">What's included:</h3>
          <ul className="space-y-3">
            {status?.tier === "pro" ? (
              <>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="text-[#3af062]">✓</span>
                  Unlimited messages & conversations
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="text-[#3af062]">✓</span>
                  Access to ALL AI models
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="text-[#3af062]">✓</span>
                  Video & audio file support
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="text-[#3af062]">✓</span>
                  Priority support
                </li>
              </>
            ) : (
              <>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="text-[#3af062]">✓</span>
                  200 messages per day
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="text-[#3af062]">✓</span>
                  Unlimited conversations
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="text-[#3af062]">✓</span>
                  Basic AI models access
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="text-[#3af062]">✓</span>
                  Audio file support
                </li>
              </>
            )}
          </ul>
        </div>

        {/* Redirect notice */}
        <p className="text-gray-500 text-sm mb-6">
          Redirecting to home in {countdown} seconds...
        </p>

        {/* Manual navigation */}
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-[#3af062] text-black font-semibold rounded-xl hover:bg-[#2dd54f] transition-colors"
          >
            Start Chatting
          </button>
          <button
            onClick={() => navigate("/profile")}
            className="px-6 py-3 bg-gray-700 text-white font-semibold rounded-xl hover:bg-gray-600 transition-colors"
          >
            View Profile
          </button>
        </div>

        {/* Session ID for reference */}
        {sessionId && (
          <p className="mt-8 text-gray-600 text-xs">
            Session ID: {sessionId.substring(0, 20)}...
          </p>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
